import { useState } from "react";
import { Filter, Maximize2, Share2, Sparkles, ZoomIn, ZoomOut } from "lucide-react";

const filterGroups = [
  {
    label: "学科",
    options: [
      { name: "全部学科", count: 3218 },
      { name: "生物学", count: 1054, active: true },
      { name: "计算机科学", count: 892 },
      { name: "医学与健康", count: 642 },
      { name: "工程与技术", count: 292 },
    ],
  },
  {
    label: "来源",
    options: [
      { name: "全部来源", count: 3218 },
      { name: "文献", count: 1782 },
      { name: "项目成果", count: 426 },
      { name: "方法笔记", count: 238 },
      { name: "数据集", count: 321 },
    ],
  },
];

const graphNodes = [
  { id: "center1", label: "单细胞转录组", x: 40, y: 48, size: "lg", color: "#3D69E8", icon: "ri-flask-line" },
  { id: "center2", label: "图神经网络", x: 61, y: 54, size: "lg", color: "#8B5CF6", icon: "ri-share-circle-line" },
  { id: "n1", label: "生物信息学", x: 69, y: 20, size: "md", color: "#10B981", icon: "ri-dna-line" },
  { id: "n2", label: "实验设计", x: 18, y: 29, size: "md", color: "#F59E0B", icon: "ri-test-tube-line" },
  { id: "n3", label: "项目成果", x: 82, y: 38, size: "md", color: "#EF4444", icon: "ri-trophy-line" },
  { id: "n4", label: "数据集", x: 26, y: 71, size: "sm", color: "#F59E0B", icon: "ri-database-2-line" },
  { id: "n5", label: "方法笔记", x: 50, y: 79, size: "sm", color: "#EC4899", icon: "ri-book-2-line" },
  { id: "n6", label: "文献合集", x: 73, y: 72, size: "sm", color: "#8B5CF6", icon: "ri-stack-line" },
];

const aiInsights = [
  "细胞互作网络推断：利用 GNN 建模细胞-细胞通信网络",
  "细胞类型注释与聚类优化：结合 GNN 特征提升细胞识别",
  "轨迹推断与动态建模：GNN 捕捉细胞发育拓扑结构",
  "多模态整合：整合 scATAC + scRNA，构建异构图学习",
  "药物反应预测：基于细胞图谱预测药物敏感性与机制",
];

export default function KnowledgeGraphView() {
  const [activeFilter, setActiveFilter] = useState<Record<string, string>>({ 学科: "生物学", 来源: "全部来源" });

  return (
    <div className="space-y-4">
      <div className="mx-auto w-full max-w-[1280px] rounded-[28px] border border-white/70 bg-white/80 px-4 py-3 shadow-[0_10px_30px_rgba(15,23,42,0.04)] backdrop-blur">
        <div className="flex items-center justify-start">
          <span className="text-sm font-semibold text-foreground">筛选条件</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-start gap-2">
          {filterGroups.map((group) => (
            <div key={group.label} className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">{group.label}</span>
              {group.options.map((opt) => (
                <button
                  key={opt.name}
                  onClick={() => setActiveFilter((f) => ({ ...f, [group.label]: opt.name }))}
                  className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                    activeFilter[group.label] === opt.name
                      ? "bg-primary/10 font-medium text-primary"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  {opt.name} <span className="opacity-70">{opt.count.toLocaleString()}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto flex min-h-[700px] w-full max-w-[1280px] gap-5">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-[28px] bg-white/85 shadow-[0_18px_60px_rgba(15,23,42,0.06)] ring-1 ring-white/70 backdrop-blur">
          <div className="flex items-center gap-2 border-b border-white/70 px-4 py-3">
            <button className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-2 text-xs text-muted-foreground hover:bg-accent">
              <Filter className="h-3.5 w-3.5" />
              布局: 力导向
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-2 text-xs text-muted-foreground hover:bg-accent">
              <Sparkles className="h-3.5 w-3.5" />
              展开关系
            </button>
            <button className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-2 text-xs text-muted-foreground hover:bg-accent">
              <Share2 className="h-3.5 w-3.5" />
              分享视图
            </button>
            <div className="ml-auto flex items-center gap-1">
              <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent"><ZoomIn className="h-4 w-4" /></button>
              <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent"><ZoomOut className="h-4 w-4" /></button>
              <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent"><Maximize2 className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="relative min-h-[620px] flex-1 overflow-hidden bg-gradient-to-br from-[#FAFBFF] via-white to-[#F8FAFE]">
            <svg className="pointer-events-none absolute inset-0 h-full w-full">
              <defs>
                <linearGradient id="graph-line" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#C8D5F8" stopOpacity="0.15" />
                  <stop offset="50%" stopColor="#C8D5F8" stopOpacity="0.65" />
                  <stop offset="100%" stopColor="#C8D5F8" stopOpacity="0.15" />
                </linearGradient>
              </defs>
              <line x1="40%" y1="48%" x2="61%" y2="54%" stroke="url(#graph-line)" strokeWidth="1.8" />
              <line x1="61%" y1="54%" x2="69%" y2="20%" stroke="url(#graph-line)" strokeWidth="1.2" />
              <line x1="40%" y1="48%" x2="18%" y2="29%" stroke="url(#graph-line)" strokeWidth="1.2" />
              <line x1="61%" y1="54%" x2="82%" y2="38%" stroke="url(#graph-line)" strokeWidth="1.2" />
              <line x1="40%" y1="48%" x2="26%" y2="71%" stroke="url(#graph-line)" strokeWidth="1" />
              <line x1="50%" y1="79%" x2="40%" y2="48%" stroke="url(#graph-line)" strokeWidth="1" />
              <line x1="61%" y1="54%" x2="73%" y2="72%" stroke="url(#graph-line)" strokeWidth="1" />
            </svg>

            {graphNodes.map((node) => {
              const sizes = {
                lg: { outer: "h-[88px] w-[88px]", inner: "h-6 w-6", text: "text-sm font-semibold" },
                md: { outer: "h-[62px] w-[62px]", inner: "h-4 w-4", text: "text-xs font-medium" },
                sm: { outer: "h-[48px] w-[48px]", inner: "h-3.5 w-3.5", text: "text-[10px]" },
              };
              const s = sizes[node.size as keyof typeof sizes];
              return (
                <div
                  key={node.id}
                  className="group absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1"
                  style={{ left: `${node.x}%`, top: `${node.y}%` }}
                >
                  <div
                    className={`${s.outer} flex items-center justify-center rounded-full border border-white/60 shadow-[0_10px_30px_rgba(61,105,232,0.08)] transition-transform group-hover:scale-110`}
                    style={{
                      backgroundColor: `${node.color}14`,
                      boxShadow: `0 0 0 8px ${node.color}08`,
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                    }}
                  >
                    <i className={`${node.icon} ${s.inner}`} style={{ color: node.color }} />
                  </div>
                  <span className={`${s.text} whitespace-nowrap text-foreground`}>{node.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex w-[320px] shrink-0 flex-col rounded-[28px] border border-white/70 bg-white/85 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.06)] backdrop-blur">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <span className="text-sm font-semibold text-foreground">AI 洞察</span>
            </div>
            <span className="text-[10.5px] text-muted-foreground">由 ScienceOne AI 生成</span>
          </div>

          <div className="mb-3 text-sm font-semibold leading-6 text-foreground">单细胞转录组与图神经网络有哪些交叉研究方向？</div>
          <div className="mb-4 text-sm leading-6 text-muted-foreground">
            基于索引图谱分析，二者在数据建模、聚类推断与群体预测等方向交叉，共同推动了高通量数据整合与图深度学习的进展。
          </div>

          <div className="mb-4">
            <div className="mb-2 text-sm font-semibold text-foreground">关键交叉方向</div>
            <ol className="space-y-1.5">
              {aiInsights.map((insight, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-0.5 text-xs font-semibold text-primary">{i + 1}</span>
                  <span className="text-sm leading-6 text-muted-foreground">{insight}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="mb-4">
            <div className="mb-2 text-sm font-semibold text-foreground">主要来源</div>
            <div className="space-y-2 text-sm text-muted-foreground">
              {[
                "Nature Methods, 2023 — Graph neural networks for single-cell omics",
                "Genome Biology, 2023 — scGNN for cell-cell communication",
                "Nature Communications, 2023 — GNN integration of scRNA+scATAC",
              ].map((s, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-xs">{i + 1}.</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-semibold text-foreground">你可以继续问</div>
            <div className="space-y-2">
              {[
                "图神经网络在单细胞领域的最新进展有哪些？",
                "有哪些公数据集适合构建细胞建模对比？",
              ].map((q, i) => (
                <button key={i} className="w-full rounded-xl px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
